import { useCallback, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import debounce from 'lodash.debounce';

import WorkOrderContext, {
  WorkOrderActionTypes,
} from '@atom/components/workOrderDetail/WorkOrderContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { DatePicker } from '@atom/mui';
import { Task, TaskUpdateInput } from '@atom/types/task';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';

import classes from './taskDetailView.module.css';

const DEBOUNCE_TIME = 1000;

const TaskStartDate = () => {
  const { workOrderDetail, task, dispatch } = useContext(WorkOrderContext);
  const [startTime, setStartTime] = useState<Date>(
    task.startTime ? new Date(task.startTime) : null,
  );

  const [taskUpdate] = useMutation<
    { taskUpdate: Task },
    { input: TaskUpdateInput }
  >(TASK_UPDATE);

  useEffect(() => {
    setStartTime(task.startTime ? new Date(task.startTime) : null);
  }, [task.startTime]);

  const setTask = (updatedTask: Task) => {
    dispatch({
      type: WorkOrderActionTypes.SET_TASK,
      data: {
        ...updatedTask,
      },
    });
  };

  const handleSubmit = async (date: Date) => {
    const res = await taskUpdate({
      variables: {
        input: {
          id: task.id,
          workOrderId: workOrderDetail.id,
          startTime: convertDateToMillisGMTMidday(date),
        },
      },
    });

    setTask(res?.data?.taskUpdate);
  };

  const debouncedHandleSubmit = useCallback(
    debounce(handleSubmit, DEBOUNCE_TIME),
    [task.id, workOrderDetail.id],
  );

  const onChange = (date?: Date) => {
    setStartTime(date);

    if (!date) {
      return;
    } else if (!Number.isNaN(date.valueOf())) {
      debouncedHandleSubmit(date);
    }
  };

  const isDisabled =
    workOrderDetail.isClosed || doesNotHaveRolePermissions(ROLE_SETS.MANAGER);

  return (
    <div className={clsx(classes.timeRowItem, classes.input)}>
      <div className={classes.label}>Start Date</div>
      <div className={classes.timePickerContainer} id="startDate">
        <DatePicker
          value={startTime}
          onChange={onChange}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
};

export default TaskStartDate;
